// Create Thread dialog — entry point from the Threads tab.
//
// Restores the affordance that was removed when the Test Audio Files feature
// shipped. The previous flow assumed users would go to /test-audio and click
// "Run", but the natural place to create a thread is the Threads tab itself.
//
// This dialog is intentionally a smaller-scope variant of `RunTestAudioDialog`:
// no inline WaveSurfer preview (we keep the dialog light when the user is
// browsing /threads, not /test-audio), but the same backend trigger
// (`POST /api/v1/threads/bulk-import` with `test_audio_file_id`).
//
// Empty-state: if the org has zero READY test audio files we surface a
// "No test audio yet" message + a link to /test-audio so the user can
// upload one.
//
// The component accepts overridable function props so tests can inject
// fakes without hitting the network or `useNavigate`.

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import { listTestAudioFiles } from "../services/testAudioService";
import { bulkImportThread } from "../services/embed";

function formatDuration(ms) {
  if (!ms || ms <= 0) return "0:00";
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function isoTimestampLocal() {
  // Mirrors the slice format `new Date().toISOString().slice(0, 16)` from the
  // brief — but in local time so the user sees a meaningful timestamp.
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CreateThreadDialog = ({
  isOpen,
  onClose,
  // Test injection points.
  listFn,
  bulkImportFn,
  navigateFn,
}) => {
  const toast = useToast();
  const _navigate = useNavigate();
  const navigate = navigateFn || _navigate;
  const doList = listFn || listTestAudioFiles;
  const doBulkImport = bulkImportFn || bulkImportThread;

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [title, setTitle] = useState("");
  const [durationMs, setDurationMs] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state every time the dialog opens; load the READY test audio list.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setSearch("");
    setSelectedFileId(null);
    setTitle("");
    setDurationMs(0);
    setError(null);
    setFilesError(null);
    setFilesLoading(true);
    (async () => {
      try {
        const response = await doList({ status: "READY", limit: 100 });
        if (cancelled) return;
        const items = response?.items || [];
        setFiles(items);
      } catch (e) {
        if (cancelled) return;
        setFilesError(
          e?.response?.data?.detail || e?.message || "Failed to load test audio files.",
        );
      } finally {
        if (!cancelled) setFilesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, doList]);

  const selectedFile = useMemo(
    () => files.find((f) => f.id === selectedFileId) || null,
    [files, selectedFileId],
  );

  const maxDurationMs = Number(selectedFile?.duration_ms || 0);

  // When the user picks a file, prefill title + duration to sensible defaults.
  useEffect(() => {
    if (!selectedFile) return;
    setTitle(`${selectedFile.name} - ${isoTimestampLocal()}`);
    setDurationMs(Number(selectedFile.duration_ms || 0));
  }, [selectedFile]);

  const filteredFiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => {
      const hay = `${f.name || ""} ${f.description || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [files, search]);

  const handleSlider = (v) => {
    if (!maxDurationMs) return;
    const clamped = Math.max(1, Math.min(Number(v) || 0, maxDurationMs));
    setDurationMs(clamped);
  };

  const canSubmit =
    !submitting &&
    selectedFile != null &&
    title.trim().length > 0 &&
    durationMs > 0;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await doBulkImport({
        testAudioFileId: selectedFile.id,
        title: title.trim(),
        durationMs,
      });
      toast({
        title: "Thread created",
        description: "Bulk import has started.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose?.();
      navigate(`/threads/${created.thread_id}`);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Bulk import failed.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const showEmptyState = !filesLoading && !filesError && files.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} size="xl">
      <ModalOverlay />
      <ModalContent data-testid="create-thread-dialog">
        <ModalHeader>Create Thread from Test Audio</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {filesLoading && (
              <Box py={6} textAlign="center">
                <Spinner size="md" />
              </Box>
            )}

            {filesError && (
              <Alert
                status="error"
                borderRadius="md"
                data-testid="create-thread-files-error"
              >
                <AlertIcon />
                <Text fontSize="sm">{filesError}</Text>
              </Alert>
            )}

            {showEmptyState && (
              <Box
                py={8}
                textAlign="center"
                bg="gray.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                borderStyle="dashed"
                data-testid="create-thread-empty"
              >
                <Text color="gray.600" fontSize="sm" mb={2}>
                  No test audio yet.
                </Text>
                <Link
                  as={RouterLink}
                  to="/test-audio"
                  color="blue.500"
                  fontSize="sm"
                  fontWeight="medium"
                  onClick={() => onClose?.()}
                  data-testid="create-thread-upload-link"
                >
                  Upload one →
                </Link>
              </Box>
            )}

            {!filesLoading && !filesError && files.length > 0 && (
              <>
                <FormControl>
                  <FormLabel>Pick test audio</FormLabel>
                  <InputGroup size="sm" mb={2}>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search by name or description"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      data-testid="create-thread-search"
                    />
                  </InputGroup>
                  <Box
                    maxH="220px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    data-testid="create-thread-file-list"
                  >
                    {filteredFiles.length === 0 && (
                      <Box py={6} textAlign="center">
                        <Text fontSize="sm" color="gray.500">
                          No matches.
                        </Text>
                      </Box>
                    )}
                    {filteredFiles.map((f) => {
                      const active = f.id === selectedFileId;
                      return (
                        <Box
                          key={f.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          bg={active ? "blue.50" : "transparent"}
                          borderLeftWidth="3px"
                          borderLeftColor={active ? "blue.400" : "transparent"}
                          _hover={{ bg: active ? "blue.50" : "gray.50" }}
                          onClick={() => setSelectedFileId(f.id)}
                          data-testid={`create-thread-file-${f.id}`}
                        >
                          <Text fontSize="sm" fontWeight="medium">
                            {f.name}
                          </Text>
                          {f.description && (
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                              {f.description}
                            </Text>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            {formatDuration(f.duration_ms)}
                          </Text>
                        </Box>
                      );
                    })}
                  </Box>
                </FormControl>

                <FormControl isRequired isDisabled={!selectedFile || submitting}>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    isDisabled={!selectedFile || submitting}
                    data-testid="create-thread-title-input"
                  />
                </FormControl>

                <FormControl
                  isDisabled={!maxDurationMs || submitting}
                  data-testid="create-thread-duration-form"
                >
                  <FormLabel>
                    Duration to import: {formatDuration(durationMs)}
                    {maxDurationMs ? (
                      <Text as="span" color="gray.500" fontSize="sm" ml={2}>
                        (max {formatDuration(maxDurationMs)})
                      </Text>
                    ) : null}
                  </FormLabel>
                  <Slider
                    min={1}
                    max={maxDurationMs || 1}
                    step={1000}
                    value={durationMs}
                    onChange={handleSlider}
                    isDisabled={!maxDurationMs || submitting}
                    aria-label="create-thread-duration-slider"
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb data-testid="create-thread-duration-thumb" />
                  </Slider>
                  <FormHelperText>
                    Defaults to the full file. Drag to import only a prefix.
                  </FormHelperText>
                </FormControl>
              </>
            )}

            {error && (
              <Alert
                status="error"
                borderRadius="md"
                data-testid="create-thread-error"
              >
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            mr={3}
            onClick={onClose}
            variant="ghost"
            isDisabled={submitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreate}
            isDisabled={!canSubmit}
            isLoading={submitting}
            data-testid="create-thread-submit-button"
          >
            Create Thread
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { formatDuration };
export default CreateThreadDialog;
